import React, { useMemo, useRef } from 'react';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { EventPageType } from '@graphql/generated-base';
import type { BoxProps } from '@withjoy/joykit';
import { NoSsr } from '@withjoy/joykit';
import type { LayoutAlohaProps } from '../../LayoutAloha';
import type { PreparedPages } from './Body.constants';
import { pageWidgets } from './Body.constants';
import Page from '../Page';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { SCREENS } from '@shared/types/fontPackScreens';
import { useIsStaysRedesignActive } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Honeymoon/useIsStaysRedesignActive';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';

export interface BodyProps
  extends Readonly<{ readonly page: Maybe<EventPageFragment>; readonly applicatorProps?: object; readonly isWelcomePage?: boolean } & LayoutAlohaProps & BoxProps> {}

export const Body: React.FC<BodyProps> = ({ event, eventHandle, page, applicatorProps = {}, isWelcomePage }) => {
  const pageRef = useRef<HTMLDivElement>(null);
  // Honeymoon-redesign URL-deep-linked page bypasses <NoSsr> so the PageWidget renders SSR (PRNT-4234).
  const isStaysRedesignActive = useIsStaysRedesignActive();
  const { currentPageSlug } = useCurrentPageSlug(eventHandle);

  const onPageEditClick = useEventCallback(() => {
    if (page) {
      sendMessageToParentWindow({
        action: 'inlineEditingInteraction',
        source: 'joyPreview',
        value: {
          action: 'editPage',
          inlineEditData: {
            eventHandle,
            pageSlug: page.pageSlug,
            pageType: page.type,
            pageTitle: page.pageTitle,
          },
        },
      });
    }
  });

  const handleEditFontClick = useEventCallback(() => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData: { screen: SCREENS.categoryList } },
    });
  });

  const { isTidbitsPage, isStoryPage, marginTop, isAccommodations } = useMemo(() => {
    return {
      isTidbitsPage: page?.type === 'tidbits',
      isStoryPage: page?.type === 'story',
      marginTop: isWelcomePage ? '0' : '4rem',
      isRegistryPage: page?.type === 'registry',
      isAccommodations: page?.pageSlug === 'accommodations',
    };
  }, [page?.pageSlug, page?.type, isWelcomePage]);

  if (!page) {
    return null;
  }

  const { type } = page;
  const PageWidget = pageWidgets[type as PreparedPages];

  const pageProps = (!isTidbitsPage && {
    title: isWelcomePage ? undefined : page.pageTitle,
    subtitle: !isStoryPage ? page.subTitle : undefined,
    graphicAccent: undefined,
    isAccommodations: isAccommodations,
    isWelcomePage,
    marginTop,
    photo: isWelcomePage ? undefined : page.photoV2,
  }) || {
    title: isWelcomePage ? undefined : page.pageTitle,
    marginTop,
  };

  if (!PageWidget) {
    return null;
  }

  const widget = (
    <PageWidget
      isAccommodations={isAccommodations}
      isWelcomePage={isWelcomePage}
      event={event}
      eventHandle={eventHandle}
      {...applicatorProps}
      pageId={page.id}
      pageSlug={page.pageSlug}
      pageTitle={pageProps.title}
    />
  );

  return (
    <InlineEditor
      actionData={{
        edit: onPageEditClick,
        font: handleEditFontClick,
      }}
      inset={{ x: 4, y: -16 }}
      wrapperType="actionOutside"
      componentName="page"
      elementLabel={page.pageTitle}
      pageName={page.type}
      pageSlug={page.pageSlug}
      stickyOnScroll={true}
      isEligibleForInlineEditing={page?.type !== EventPageType.app}
    >
      <Page
        {...pageProps}
        visibility={page.visibility}
        pageSlug={page.pageSlug}
        ref={pageRef}
        pageId={page.id}
        pageType={page.type}
        isPageContainer={page.isPageContainer}
      >
        {/* Welcome page + honeymoon-redesign current page render SSR; others NoSsr-wrapped (PR #14490, PRNT-4234). */}
        {isWelcomePage || (isStaysRedesignActive && page.pageSlug === currentPageSlug) ? widget : <NoSsr>{widget}</NoSsr>}
      </Page>
    </InlineEditor>
  );
};
